import { FileEntityType, PreviewFormatsType, UploadFileResponse, WithPreviewFileType } from 'src/api/api-types/files';
import { FilesApi } from 'src/api/files';
import { action, computed, makeObservable, observable } from 'mobx';
import last from 'lodash/last';
import { ApiReq, emptyValue } from 'src/api';

const imageExtensions = [
  'jpg',
  'JPG',
  'jpeg',
  'image/jpeg',
  'png',
  'image/png',
  'gif',
  'svg',
  'avif',
  'image/avif',
  'image/svg+xml',
];

const pdfExtensions = ['pdf', 'application/pdf'];

export class FileBase {
  readonly id: number;
  readonly fileName: string;
  readonly description?: string;
  readonly createdBy?: string;
  readonly fileServiceId: number;
  readonly createdDate?: string;
  readonly api = new FilesApi();
  readonly previewFormats: PreviewFormatsType[] = [];

  @observable.ref filesReq: ApiReq<Blob> = emptyValue;
  @observable.ref filePreviewReq: ApiReq<Blob> = emptyValue;
  @observable.ref filePreviewSmallReq: ApiReq<Blob> = emptyValue;
  @observable.ref filePreviewMediumReq: ApiReq<Blob> = emptyValue;
  @observable.ref deleteReq: ApiReq<UploadFileResponse> = emptyValue;
  @observable.ref deleteFromServiceReq: ApiReq<UploadFileResponse> = emptyValue;

  constructor(file: FileEntityType & Partial<WithPreviewFileType>) {
    makeObservable(this);

    this.id = file.key || file.id;
    this.fileName = file.name || file.fileName || 'default_name';
    this.description = file.description;
    this.fileServiceId = file.fileServiceId;
    this.createdDate = file.createdDate;
    this.previewFormats = file.previewFormats || [];

    if (typeof file.createdBy === 'object' && file.createdBy !== null) {
      this.createdBy = file.createdBy.displayText;
    } else {
      this.createdBy = file.createdBy;
    }
  }

  @computed get isAvifPreview() {
    return Boolean(this.previewFormats?.some(format => format.name === 'avif'));
  }

  @computed get isJpgPreview() {
    return Boolean(this.previewFormats?.some(format => format.name === 'jpg'));
  }

  @computed get isPdfPreview() {
    return Boolean(this.previewFormats?.some(format => format.name === 'pdf'));
  }

  @computed get extension() {
    return (last(this.fileName.split('.')) || '').toLowerCase();
  }

  @computed get isImage() {
    return imageExtensions.includes(this.extension);
  }

  @computed get isPdf() {
    return pdfExtensions.includes(this.extension);
  }

  @computed get src() {
    if (this.filesReq.state === 'fulfilled' && !!this.filesReq.value?.data) {
      return window.URL.createObjectURL(
        new Blob([this.filesReq.value.data as Blob]),
      );
    }
    return '';
  }

  @action async fetchSrc() {
    if (!!this.src) return;

    this.filesReq = this.api.getFilesUrl(this.fileServiceId);

    await this.filesReq;
  }

  @action delete = () => {
    this.deleteReq = this.api.deleteFile(this.id);
    this.deleteFromServiceReq = this.api.deleteFileFromService(
      this.fileServiceId,
    );

    return Promise.allSettled([this.deleteReq, this.deleteFromServiceReq]);
  };

  @action download = async () => {
    await this.fetchSrc();
    const element = document.createElement('a');
    element.setAttribute('href', this.src);
    element.setAttribute('download', this.fileName);
    element.style.display = 'none';
    document.body.appendChild(element);
    element.click();
    element.remove();
  };
}
